export const FP_STORAGE_KEY = 'auth_fp';
export const BOARD_SOUND_ENABLED_KEY = 'board_sound_enabled';
export const ROLES_STORAGE_KEY = 'roles';
export const ANALYTICS_ALLOWED_ROLES = ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_SPECIALIST_TS'];
export const IT_TS_ALLOWED_ROLES = ['ROLE_SPECIALIST_TS', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'];
export const USER_MANAGEMENT_ALLOWED_ROLES = ['ROLE_SUPER_ADMIN'];
export const SHIFT_EDIT_ALLOWED_ROLES = ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN'];
const ROLES_UPDATED_EVENT = 'support:roles-updated';

function getStorage() {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

function normalizeRoles(value) {
    if (!Array.isArray(value)) return [];

    return Array.from(
        new Set(
            value
                .map((role) => String(role || '').trim())
                .filter(Boolean)
        )
    );
}

function dispatchRolesUpdated(roles) {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
        return;
    }

    try {
        window.dispatchEvent(new CustomEvent(ROLES_UPDATED_EVENT, { detail: roles }));
    } catch {
        // ignore browser event failures
    }
}

export function getRolesFromStorage() {
    const storage = getStorage();
    if (!storage) return [];

    try {
        const raw = storage.getItem(ROLES_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return normalizeRoles(parsed);
    } catch {
        return [];
    }
}

export function persistRoles(roles) {
    const storage = getStorage();
    const normalizedRoles = normalizeRoles(roles);

    if (storage) {
        try {
            if (normalizedRoles.length > 0) {
                storage.setItem(ROLES_STORAGE_KEY, JSON.stringify(normalizedRoles));
            } else {
                storage.removeItem(ROLES_STORAGE_KEY);
            }
        } catch {
            // ignore storage failures
        }
    }

    dispatchRolesUpdated(normalizedRoles);

    return normalizedRoles;
}

export function clearRoles() {
    return persistRoles([]);
}

export function subscribeToRoles(callback) {
    if (typeof window === 'undefined' || typeof callback !== 'function') {
        return () => {};
    }

    const handleRolesUpdated = (event) => {
        callback(normalizeRoles(event?.detail));
    };

    const handleStorage = (event) => {
        if (event.key !== ROLES_STORAGE_KEY) return;

        try {
            const parsed = event.newValue ? JSON.parse(event.newValue) : [];
            callback(normalizeRoles(parsed));
        } catch {
            callback([]);
        }
    };

    window.addEventListener(ROLES_UPDATED_EVENT, handleRolesUpdated);
    window.addEventListener('storage', handleStorage);

    return () => {
        window.removeEventListener(ROLES_UPDATED_EVENT, handleRolesUpdated);
        window.removeEventListener('storage', handleStorage);
    };
}

export function hasAnyRole(roles, allowedRoles = []) {
    if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) {
        return true;
    }

    const normalizedRoles = Array.isArray(roles) ? roles : [];
    return allowedRoles.some((role) => normalizedRoles.includes(role));
}

export function hasStoredRole(allowedRoles = []) {
    return hasAnyRole(getRolesFromStorage(), allowedRoles);
}

export function getFingerprintFromCookie() {
    try {
        const match = document.cookie.match(/(?:^|;\s*)fp=([^;]+)/);
        return match ? decodeURIComponent(match[1]) : '';
    } catch {
        return '';
    }
}

export function getStoredFingerprint() {
    const storage = getStorage();
    if (!storage) return '';

    try {
        return storage.getItem(FP_STORAGE_KEY) || '';
    } catch {
        return '';
    }
}

export function persistFingerprint(value) {
    const storage = getStorage();
    if (!storage) return '';

    const normalized = String(value || '').trim();

    try {
        if (normalized) {
            storage.setItem(FP_STORAGE_KEY, normalized);
        } else {
            storage.removeItem(FP_STORAGE_KEY);
        }
    } catch {
        // ignore storage failures
    }

    return normalized;
}

export function syncFingerprintFromCookie() {
    const cookieFp = getFingerprintFromCookie();
    if (cookieFp) {
        persistFingerprint(cookieFp);
        return cookieFp;
    }
    return getStoredFingerprint();
}

export function getFingerprintForHeader() {
    return syncFingerprintFromCookie() || getStoredFingerprint();
}

export function getInitialBoardSoundEnabled() {
    const storage = getStorage();
    if (!storage) return true;

    try {
        const raw = storage.getItem(BOARD_SOUND_ENABLED_KEY);
        if (raw === '0' || raw === 'false') return false;
        if (raw === '1' || raw === 'true') return true;
    } catch {
        // ignore storage failures
    }

    return true;
}

export function persistBoardSoundEnabled(value) {
    const storage = getStorage();
    if (!storage) return;

    try {
        storage.setItem(BOARD_SOUND_ENABLED_KEY, value ? '1' : '0');
    } catch {
        // ignore storage failures
    }
}
